import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/AppLayout';
import { DateRangeFilter } from '@/components/reports/DateRangeFilter';
import { MetricCard } from '@/components/reports/MetricCard';
import { WeeklyTrendChart } from '@/components/reports/WeeklyTrendChart';
import { PlanUsageBar } from '@/components/reports/PlanUsageBar';
import { usePlatformReport } from '@/hooks/useReports';

function getDefaultDates() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 90);
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

export default function PlatformReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const { data, isLoading, error } = usePlatformReport(startDate, endDate);

  const isAdminOrOwner = user?.role === 'owner' || user?.role === 'admin';

  if (!isAdminOrOwner) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-red-600">Acesso restrito a administradores.</p>
          <button onClick={() => navigate('/reports')} className="mt-2 text-sm text-blue-600 hover:underline">← Voltar</button>
        </div>
      </AppLayout>
    );
  }

  // Adaptar monthlyUsage para WeeklyTrendChart (reutilizando o componente)
  const monthlyData = data?.monthlyUsage.map((m) => ({
    week: m.month,
    count: m.jobsCreated + m.resumesProcessed,
  })) || [];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Relatório da Plataforma</h1>
          <DateRangeFilter startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
        </div>

        {isLoading && <p className="text-gray-500">Carregando relatório...</p>}
        {error && <p className="text-red-600">Erro ao carregar: {error.message}</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard label="Vagas Criadas" value={data.totalJobsCreated} />
              <MetricCard label="Currículos Processados" value={data.totalResumesProcessed} />
              <MetricCard label="Entrevistas" value={data.totalInterviews} />
              <MetricCard label="Sessões Chat IA" value={data.totalChatSessions} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <WeeklyTrendChart data={monthlyData} title="Evolução Mensal (Vagas + Currículos)" />
              <PlanUsageBar data={data.planUsage} />
            </div>

            <div className="bg-white rounded-lg border p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Métricas de IA</h3>
              <div className="grid grid-cols-3 gap-4">
                <MetricCard label="Fit Score Médio" value={data.aiMetrics.averageFitScore.toFixed(1)} />
                <MetricCard label="Interview Score Médio" value={data.aiMetrics.averageInterviewScore.toFixed(1)} />
                <MetricCard label="Invocações Bedrock" value={data.aiMetrics.totalBedrockInvocations} />
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
